import {
    Body,
    breakpoints,
    Button,
    colors,
    Dropdown,
    Margin,
    MoreSvg,
    Spacer,
} from '@spritz-finance/ui'
import dayjs from 'dayjs'
import React, { useState } from 'react'
import styled, { css } from 'styled-components'
import { KeyValuePill } from '../../../../components/KeyValuePill'
import { Card } from '../../../../components/Layout/Card'
import { Column } from '../../../../components/Layout/Column'
import { Row } from '../../../../components/Layout/Row'
import { useScreenSize } from '../../../../context/useScreenSize'
import { BillSyncStatus } from '../../../../graphql/generated/graphql'
import { cssVar } from '../../../../theme/utils'
import { useBillDetails } from '../../utils/useBillDetails'
import { DirectPayButton } from '../directPay/DirectPayButton'
import { InstitutionImage } from '../InstitutionImage'
import { BillIcon, typeDescription } from './BillIcon'
import { Bill, Subscription } from './Bills'
import { SmartPayActions } from './subscriptions/SmartPayActions'
import { useActiveSubscription } from './subscriptions/useActiveSubscription'
import { TestPayInfo } from './TestPay'

const getLocalisedDate = (paymentDate?: string | null) => {
    if (!paymentDate) return null
    const myRegexp = /^[0-9]{4}-[0-9]{2}-[0-9]{2}/g
    const date = paymentDate.match(myRegexp)?.[0] ?? null
    return date ? dayjs(`${date} 09:00:00`) : null
}

const getDaysUntil = (date?: dayjs.Dayjs | null) => {
    if (!date) return null
    const hoursBetween = dayjs(date).diff(dayjs(), 'hours')
    return Math.abs(Math.ceil(hoursBetween / 24))
}

export const InfoContainer = styled.div`
    padding: 16px;
    border-radius: 10px;
    background: var(--surface-3, ${colors.seaSpray[50]});
    display: flex;
    align-items: center;
    justify-content: center;
`

const BillTitle = ({ bill }: { bill: Bill }) => {
    return (
        <Row>
            <Row>
                <InstitutionImage size={47} institution={bill.institution} />
            </Row>
            <Spacer width={16} />
            <Column>
                <Body.Med secondary size={14}>
                    {bill.institution?.name}
                </Body.Med>
                <Spacer height={4} />
                <Body.Bold size={18}>{bill.name}</Body.Bold>
            </Column>
        </Row>
    )
}

const SyncStatus = ({ bill }: { bill: Bill }) => {
    const disabled = !bill.canReceive

    if (disabled) {
        return (
            <>
                <Column>
                    <InfoContainer>
                        <Body.Med size={14} align="center">
                            Hang tight! We're currently verifying this bill for you. Sit back and
                            relax, we'll let you know when it’s ready to make a payment.
                        </Body.Med>
                    </InfoContainer>
                    <Spacer height={32} />
                </Column>
            </>
        )
    }

    if (bill.syncStatus === BillSyncStatus.Unavailable) {
        return (
            <>
                <Column>
                    <InfoContainer>
                        <Body.Med size={14} align="center">
                            Your bill statement details can't be displayed right now, but you can
                            still make payments.
                        </Body.Med>
                    </InfoContainer>
                    <Spacer height={32} />
                </Column>
            </>
        )
    }

    switch (bill.syncStatus) {
        case BillSyncStatus.Complete:
        case BillSyncStatus.InProgress:
            if (!bill.lastSync) {
                return null
            }

            return (
                <Column>
                    <Body.Med size={14} secondary>
                        Lastest information sync: {dayjs(bill.lastSync).format('MMM DD, YYYY')}
                    </Body.Med>
                    <Spacer height={32} />
                </Column>
            )

        default:
            return null
    }
}

const BillBalances = ({ bill }: { bill: Bill }) => {
    const { isMobile } = useScreenSize()
    const { paymentDate, amountDue } = useBillDetails(bill)

    if (!paymentDate && !amountDue) return null

    const localisedPaymentDate = getLocalisedDate(paymentDate)
    const daysUntil = getDaysUntil(localisedPaymentDate)
    const futureDate = localisedPaymentDate ? dayjs(localisedPaymentDate).isAfter(dayjs()) : false
    const paymentText = futureDate ? 'Next payment date:' : 'Last payment due:'

    return (
        <>
            <Column align="center">
                {!!amountDue && (
                    <KeyValuePill keyText="Amount due:" color="#f2ffdc" width="130px">
                        <Body.Bold size={14}>{amountDue}</Body.Bold>
                    </KeyValuePill>
                )}
                {!!paymentDate && (
                    <>
                        <Spacer height={8} />
                        <KeyValuePill keyText={paymentText} color="#FFF2E2" width="130px">
                            <Body.Bold size={14}>
                                {dayjs(localisedPaymentDate).format('MMM DD, YYYY')}
                            </Body.Bold>
                            {!isMobile && futureDate && (
                                <>
                                    <Spacer width={8} />
                                    <Body.Med
                                        color={cssVar('--text-tertiary', colors.medGrey)}
                                        size={12}
                                    >
                                        {`${daysUntil} ${
                                            daysUntil === 1 ? 'day' : 'days'
                                        } from now`}
                                    </Body.Med>
                                </>
                            )}
                        </KeyValuePill>
                    </>
                )}
                <Spacer height={16} />
            </Column>
        </>
    )
}

export const DisplayCard = styled.div<{ $visible?: boolean }>`
    flex: 1;
    max-width: 50%;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    align-items: center;
    padding: 10px;
    background: var(--surface-2, ${colors.white});
    box-shadow: 8px 8px 20px rgba(0, 0, 0, 0.08);
    border-radius: 10px;
    height: 73px;
    box-sizing: border-box;
    min-width: 80px;
    ${({ $visible = true }) =>
        !$visible &&
        css`
            display: none;
        `};
`

const StyledCard = styled(Card)`
    background: var(--background-primary, ${colors.white});
    padding: ${Margin.Large};
    padding-top: ${Margin.Med};

    ${breakpoints.xsmall} {
        padding: ${Margin.Med};
        padding-top: ${Margin.Small};
    }
`

type BillCardProps = {
    bill: Bill
    subscriptions: Subscription[]
    onRemoveBill: (bill: Bill) => void
    onRenameBill: (bill: Bill) => void
    onChangeBillType: (bill: Bill) => void
    onDeleteSubscription: (subscription: Subscription) => void
}

export const BillCard: React.VFC<BillCardProps> = ({
    bill,
    subscriptions,
    onRemoveBill,
    onRenameBill,
    onChangeBillType,
    onDeleteSubscription,
}) => {
    const { balance, hasNoBalance } = useBillDetails(bill)
    const disabled = !bill.canReceive

    const { subscription } = useActiveSubscription(bill, subscriptions)
    const [dialogOpen, setDialogOpen] = useState(false)

    return (
        <StyledCard>
            <Row align="space-between">
                <BillTitle bill={bill} />
                <Spacer width={8} />
                <Dropdown.Menu
                    minWidth={160}
                    align="end"
                    anchor={
                        <Button.Icon
                            icon={MoreSvg}
                            fillColor
                            size={24}
                            style={{ minWidth: 0, marginRight: -12 }}
                        />
                    }
                >
                    <Dropdown.Item onClick={() => onRenameBill(bill)}>
                        <Row align="space-between">
                            <Body.Med size={14}>Rename</Body.Med>
                        </Row>
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => onChangeBillType(bill)}>
                        <Row align="space-between">
                            <Body.Med size={14}>Change bill category</Body.Med>
                        </Row>
                    </Dropdown.Item>
                    {subscription && (
                        <Dropdown.Item onClick={() => onDeleteSubscription(subscription)}>
                            <Row align="space-between">
                                <Body.Med size={14}>Cancel SMARTPay</Body.Med>
                            </Row>
                        </Dropdown.Item>
                    )}
                    <Dropdown.Item onClick={() => onRemoveBill(bill)}>
                        <Row align="space-between">
                            <Body.Med size={14}>Remove bill</Body.Med>
                        </Row>
                    </Dropdown.Item>
                </Dropdown.Menu>
            </Row>
            <TestPayInfo bill={bill} openDialog={() => setDialogOpen(true)} />
            <Spacer height={24} />
            <Row align="center">
                <DisplayCard>
                    <BillIcon type={bill.type} />
                    <Spacer height={8} />
                    <Body.Med size={14} align="center">
                        {typeDescription[bill.type]}
                    </Body.Med>
                </DisplayCard>
                <Spacer width={16} />
                <DisplayCard $visible={!hasNoBalance}>
                    <Body.Bold size={22}>{hasNoBalance ? '$' : balance}</Body.Bold>
                    <Spacer height={8} />
                    <Body.Med size={14} align="center">
                        Current balance
                    </Body.Med>
                </DisplayCard>
            </Row>
            <Spacer height={24} />
            <BillBalances bill={bill} />
            <Row align="center">
                <SyncStatus bill={bill} />
            </Row>

            <SmartPayActions bill={bill} subscriptions={subscriptions} />

            <Row align="center">
                <DirectPayButton
                    bill={bill}
                    disabled={disabled}
                    open={dialogOpen}
                    setIsOpen={setDialogOpen}
                />
            </Row>
        </StyledCard>
    )
}
